/** Versiones mínimas alineadas con `.browserslistrc` del banner (downlevel JS + layout SCSS sin depender de `@layer`). */
export const MIN_CHROME_VERSION = '93';
export const MIN_SAFARI_VERSION = '16.3';
export const MIN_FIREFOX_VERSION = '92';
export const MIN_EDGE_VERSION = '93';

export type SupportedBrowser = 'chrome' | 'safari' | 'firefox' | 'edge' | 'other';

export interface BrowserVersionInfo {
  browser: SupportedBrowser;
  version?: string;
}

export function compareVersions(current: string, minimum: string): number {
  const currentParts = current.split('.').map((part) => Number.parseInt(part, 10) || 0);
  const minimumParts = minimum.split('.').map((part) => Number.parseInt(part, 10) || 0);
  const maxLength = Math.max(currentParts.length, minimumParts.length);

  for (let index = 0; index < maxLength; index += 1) {
    const currentPart = currentParts[index] ?? 0;
    const minimumPart = minimumParts[index] ?? 0;

    if (currentPart < minimumPart) {
      return -1;
    }

    if (currentPart > minimumPart) {
      return 1;
    }
  }

  return 0;
}

export function meetsMinimumVersion(current: string, minimum: string): boolean {
  return compareVersions(current, minimum) >= 0;
}

export function detectBrowser(userAgent: string): BrowserVersionInfo {
  const ua = userAgent;

  const firefoxMatch = ua.match(/Firefox\/(\d+)/);
  if (firefoxMatch) {
    return { browser: 'firefox', version: firefoxMatch[1] };
  }

  const edgeMatch = ua.match(/Edg\/(\d+)/);
  if (edgeMatch) {
    return { browser: 'edge', version: edgeMatch[1] };
  }

  if (!/Edg\/|OPR\/|Opera/.test(ua)) {
    const chromeMatch = ua.match(/Chrome\/(\d+)/);
    if (chromeMatch) {
      return { browser: 'chrome', version: chromeMatch[1] };
    }
  }

  if (/Safari\//.test(ua) && !/Chrome\/|Chromium\//.test(ua)) {
    const safariMatch = ua.match(/Version\/(\d+(?:\.\d+)?)/);
    if (safariMatch) {
      return { browser: 'safari', version: safariMatch[1] };
    }
  }

  return { browser: 'other' };
}

export function getBrowserReportDetails(
  userAgent: string
): { browser: string; version: string } | null {
  const { browser, version } = detectBrowser(userAgent);

  if (browser === 'other' || !version) {
    return null;
  }

  const fullVersion = extractFullBrowserVersion(userAgent, browser);

  return {
    browser: browser.charAt(0).toUpperCase() + browser.slice(1),
    version: fullVersion ?? version
  };
}

function extractFullBrowserVersion(userAgent: string, browser: SupportedBrowser): string | undefined {
  switch (browser) {
    case 'firefox':
      return userAgent.match(/Firefox\/([\d.]+)/)?.[1];
    case 'chrome':
      if (!/Edg\/|OPR\/|Opera/.test(userAgent)) {
        return userAgent.match(/Chrome\/([\d.]+)/)?.[1];
      }
      return undefined;
    case 'safari':
      return userAgent.match(/Version\/([\d.]+)/)?.[1];
    case 'edge':
      return userAgent.match(/Edg\/([\d.]+)/)?.[1];
    default:
      return undefined;
  }
}

export function isBannerBrowserCompatible(userAgent: string = navigator.userAgent): boolean {
  const { browser, version } = detectBrowser(userAgent);

  if (browser === 'other' || !version) {
    return true;
  }

  switch (browser) {
    case 'chrome':
      return meetsMinimumVersion(version, MIN_CHROME_VERSION);
    case 'safari':
      return meetsMinimumVersion(version, MIN_SAFARI_VERSION);
    case 'firefox':
      return meetsMinimumVersion(version, MIN_FIREFOX_VERSION);
    case 'edge':
      return meetsMinimumVersion(version, MIN_EDGE_VERSION);
    default:
      return true;
  }
}
